import React, { useEffect, useState } from 'react';
import { apiClient } from '../../../api/client';
import {
  PRIMARY,
  TEXT_MUTED,
  BORDER_LIGHT,
  BG_CARD,
  BG_SURFACE,
  TEXT_DARK,
} from '../table-management/designTokens';

// --- Types ---

interface DuplicateRecord {
  ticketNumber: string;
  chapterAffiliation: string | null;
  orderReferenceId: string;
  purchaserName: string;
}

interface DuplicateGroup {
  guestName: string;
  records: DuplicateRecord[];
}

interface PreDeleteResult {
  orderReferenceId: string;
  purchaserName: string;
  ticketCount: number;
  guestNames: string[];
  warnings: string[];
  safe: boolean;
}

interface DeletionSummary {
  orderReferenceId: string;
  purchaserName: string;
  seatsUnlinked: number;
  checkInsDeleted: number;
  mealSelectionsDeleted: number;
  registrationsDeleted: number;
  syntheticUsersDeleted: number;
  guestRecordsDeleted: number;
  ticketsDeleted: number;
  orderDeleted: boolean;
}

interface ConsistencyIssue {
  type: string;
  description: string;
  affectedIds: string[];
  count: number;
}

interface ConsistencyReport {
  healthy: boolean;
  issues: ConsistencyIssue[];
  counts: {
    guestRecords: number;
    tickets: number;
    orders: number;
    registrations: number;
  };
}

interface AuditRecord {
  id: string;
  orderReferenceId: string;
  purchaserName: string;
  ticketNumbers: string;
  tierName: string;
  pricePerTicket: number;
  revenueImpact: number;
  guestCount: number;
  guestNames: string;
  reason: string;
  deletedById: string;
  deletedByName: string;
  deletedAt: string;
}

type DeletionReason = 'Duplicate' | 'EnteredInError' | 'Inconsistent' | 'Other';

// --- Styles ---

const sectionStyle: React.CSSProperties = {
  backgroundColor: BG_CARD,
  border: `1px solid ${BORDER_LIGHT}`,
  borderRadius: '8px',
  padding: '1.25rem',
  marginBottom: '1rem',
};

const sectionTitleStyle: React.CSSProperties = {
  fontWeight: 700,
  fontSize: '0.95rem',
  color: TEXT_DARK,
  marginBottom: '0.75rem',
  cursor: 'pointer',
  userSelect: 'none',
};

const primaryBtnStyle: React.CSSProperties = {
  backgroundColor: PRIMARY,
  color: '#fff',
  border: 'none',
  borderRadius: '6px',
  padding: '8px 16px',
  fontSize: '0.85rem',
  fontWeight: 600,
  cursor: 'pointer',
};

const destructiveBtnStyle: React.CSSProperties = {
  backgroundColor: '#d32f2f',
  color: '#fff',
  border: 'none',
  borderRadius: '6px',
  padding: '8px 16px',
  fontSize: '0.85rem',
  fontWeight: 600,
  cursor: 'pointer',
};

const tableStyle: React.CSSProperties = {
  borderCollapse: 'collapse',
  width: '100%',
  fontSize: '0.85rem',
};

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '8px 10px',
  borderBottom: `2px solid ${BORDER_LIGHT}`,
  color: TEXT_DARK,
  fontWeight: 600,
  fontSize: '0.8rem',
};

const tdStyle: React.CSSProperties = {
  padding: '6px 10px',
  borderBottom: `1px solid ${BORDER_LIGHT}`,
  color: TEXT_DARK,
};

const inputStyle: React.CSSProperties = {
  padding: '8px 12px',
  border: `1px solid ${BORDER_LIGHT}`,
  borderRadius: '6px',
  fontSize: '0.85rem',
  outline: 'none',
};

const badgeBase: React.CSSProperties = {
  display: 'inline-block',
  padding: '2px 8px',
  borderRadius: '10px',
  fontSize: '0.75rem',
  fontWeight: 600,
};

const iconBtnStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: '4px 6px',
  fontSize: '0.9rem',
  borderRadius: '4px',
  lineHeight: 1,
};

// --- Component ---

export function DataManagementTab({ user }: { user: { sub: string; role: string } | null }) {
  // Prop available for future role-based restrictions
  void user;

  // Section collapse state
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    duplicates: true,
    delete: true,
    consistency: true,
    history: true,
  });

  // Duplicate Analysis state
  const [duplicates, setDuplicates] = useState<DuplicateGroup[] | null>(null);
  const [dupLoading, setDupLoading] = useState(false);
  const [dupError, setDupError] = useState<string | null>(null);

  // Delete Order state
  const [deleteRefId, setDeleteRefId] = useState('');
  const [preDeleteResult, setPreDeleteResult] = useState<PreDeleteResult | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteReason, setDeleteReason] = useState<DeletionReason | ''>('');
  const [deleteSummary, setDeleteSummary] = useState<DeletionSummary | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Consistency Check state
  const [consistencyReport, setConsistencyReport] = useState<ConsistencyReport | null>(null);
  const [consistencyLoading, setConsistencyLoading] = useState(false);
  const [consistencyError, setConsistencyError] = useState<string | null>(null);

  // Deletion History state
  const [history, setHistory] = useState<AuditRecord[] | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);

  // Fetch deletion history on mount
  useEffect(() => {
    fetchHistory();
  }, []);

  const toggleSection = (key: string) => {
    setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // --- Duplicate Analysis ---

  const handleScanDuplicates = async () => {
    setDupLoading(true);
    setDupError(null);
    try {
      const data = await apiClient<DuplicateGroup[]>('/order-data-management/duplicates');
      setDuplicates(data);
    } catch (err: any) {
      setDupError(err?.message || 'Failed to scan for duplicates');
    } finally {
      setDupLoading(false);
    }
  };

  const handleDeleteFromDuplicate = (orderRefId: string) => {
    setDeleteRefId(orderRefId);
    setPreDeleteResult(null);
    setDeleteSummary(null);
    setDeleteReason('');
    setDeleteError(null);
    setExpandedSections((prev) => ({ ...prev, delete: true }));
    handleLookUp(orderRefId);
  };

  // --- Delete Order ---

  const handleLookUp = async (refIdOverride?: string) => {
    const refId = refIdOverride || deleteRefId;
    if (!refId.trim()) return;
    setDeleteLoading(true);
    setDeleteError(null);
    setPreDeleteResult(null);
    setDeleteSummary(null);
    setDeleteReason('');
    try {
      const data = await apiClient<PreDeleteResult>(
        '/order-data-management/orders/' + encodeURIComponent(refId.trim()) + '/pre-delete-check',
      );
      setPreDeleteResult(data);
    } catch (err: any) {
      setDeleteError(err?.message || 'Order not found');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteOrder = async () => {
    if (!preDeleteResult || !deleteReason) return;
    const confirmed = window.confirm(
      `Are you sure you want to permanently delete order "${preDeleteResult.orderReferenceId}"?\n\nThis will remove ${preDeleteResult.ticketCount} ticket(s) and all associated guest records. This action cannot be undone.`,
    );
    if (!confirmed) return;

    setDeleting(true);
    setDeleteError(null);
    try {
      const result = await apiClient<DeletionSummary>(
        '/order-data-management/orders/' + encodeURIComponent(preDeleteResult.orderReferenceId),
        { method: 'DELETE', body: { reason: deleteReason } },
      );
      setDeleteSummary(result);
      setPreDeleteResult(null);
      fetchHistory();
    } catch (err: any) {
      setDeleteError(err?.message || 'Deletion failed');
    } finally {
      setDeleting(false);
    }
  };

  const clearDeleteForm = () => {
    setDeleteRefId('');
    setPreDeleteResult(null);
    setDeleteSummary(null);
    setDeleteReason('');
    setDeleteError(null);
  };

  // --- Consistency Check ---

  const handleConsistencyCheck = async () => {
    setConsistencyLoading(true);
    setConsistencyError(null);
    try {
      const data = await apiClient<ConsistencyReport>('/order-data-management/consistency-check');
      setConsistencyReport(data);
    } catch (err: any) {
      setConsistencyError(err?.message || 'Failed to run consistency check');
    } finally {
      setConsistencyLoading(false);
    }
  };

  // --- Deletion History ---

  const fetchHistory = async () => {
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const data = await apiClient<AuditRecord[]>('/order-data-management/deletion-history');
      setHistory(data);
    } catch (err: any) {
      setHistoryError(err?.message || 'Failed to load deletion history');
    } finally {
      setHistoryLoading(false);
    }
  };

  // --- Reason badge helper ---

  const getReasonBadge = (reason: string) => {
    let bg = '#e0e0e0';
    let color = '#333';
    switch (reason) {
      case 'Duplicate':
        bg = '#fff3e0';
        color = '#e65100';
        break;
      case 'EnteredInError':
        bg = '#ffebee';
        color = '#c62828';
        break;
      case 'Inconsistent':
        bg = '#fffde7';
        color = '#f9a825';
        break;
      case 'Other':
        bg = '#f5f5f5';
        color = '#616161';
        break;
    }
    const label = reason === 'EnteredInError' ? 'Entered in Error' : reason;
    return (
      <span style={{ ...badgeBase, backgroundColor: bg, color }}>{label}</span>
    );
  };

  return (
    <div style={{ padding: '0.5rem 0' }}>
      {/* Section 1: Duplicate Analysis */}
      <div style={sectionStyle}>
        <div
          style={sectionTitleStyle}
          onClick={() => toggleSection('duplicates')}
          aria-expanded={expandedSections.duplicates}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && toggleSection('duplicates')}
        >
          {expandedSections.duplicates ? '▾' : '▸'} 🔍 Duplicate Analysis
        </div>
        {expandedSections.duplicates && (
          <div>
            <button
              style={primaryBtnStyle}
              onClick={handleScanDuplicates}
              disabled={dupLoading}
              aria-label="Scan for duplicates"
            >
              {dupLoading ? 'Scanning...' : '🔍 Scan for Duplicates'}
            </button>

            {dupError && (
              <p style={{ color: '#d32f2f', fontSize: '0.85rem', marginTop: '0.5rem' }}>{dupError}</p>
            )}

            {dupLoading && (
              <p style={{ color: TEXT_MUTED, fontSize: '0.85rem', marginTop: '0.5rem' }}>Loading...</p>
            )}

            {duplicates !== null && !dupLoading && (
              <div style={{ marginTop: '0.75rem' }}>
                {duplicates.length === 0 ? (
                  <p style={{ color: '#2e7d32', fontSize: '0.85rem', fontWeight: 600 }}>
                    No duplicates found ✓
                  </p>
                ) : (
                  <table style={tableStyle}>
                    <thead>
                      <tr>
                        <th style={thStyle}>Guest Name</th>
                        <th style={thStyle}>Ticket # (Chapter)</th>
                        <th style={thStyle}>Order Ref</th>
                        <th style={thStyle}>Ticket # (Chapter)</th>
                        <th style={thStyle}>Order Ref</th>
                        <th style={thStyle}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {duplicates.map((group) => (
                        <tr key={group.guestName}>
                          <td style={{ ...tdStyle, fontWeight: 600 }}>{group.guestName}</td>
                          {group.records.slice(0, 2).map((rec, idx) => (
                            <React.Fragment key={idx}>
                              <td style={tdStyle}>
                                {rec.ticketNumber}
                                {rec.chapterAffiliation ? ` (${rec.chapterAffiliation})` : ''}
                              </td>
                              <td style={tdStyle}>
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                                  {rec.orderReferenceId}
                                  <button
                                    style={iconBtnStyle}
                                    onClick={() => handleDeleteFromDuplicate(rec.orderReferenceId)}
                                    title={`Delete order ${rec.orderReferenceId}`}
                                    aria-label={`Delete order ${rec.orderReferenceId}`}
                                  >
                                    🗑️
                                  </button>
                                </span>
                              </td>
                            </React.Fragment>
                          ))}
                          {group.records.length < 2 && (
                            <>
                              <td style={tdStyle}></td>
                              <td style={tdStyle}></td>
                            </>
                          )}
                          <td style={tdStyle}></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Section 2: Delete Order */}
      <div style={sectionStyle}>
        <div
          style={sectionTitleStyle}
          onClick={() => toggleSection('delete')}
          aria-expanded={expandedSections.delete}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && toggleSection('delete')}
        >
          {expandedSections.delete ? '▾' : '▸'} 🗑️ Delete Order
        </div>
        {expandedSections.delete && (
          <div>
            {/* Lookup */}
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '0.75rem' }}>
              <input
                type="text"
                value={deleteRefId}
                onChange={(e) => setDeleteRefId(e.target.value)}
                placeholder="Order Reference ID"
                style={{ ...inputStyle, flex: 1, maxWidth: '300px' }}
                aria-label="Order Reference ID"
                onKeyDown={(e) => e.key === 'Enter' && handleLookUp()}
              />
              <button
                style={primaryBtnStyle}
                onClick={() => handleLookUp()}
                disabled={deleteLoading || !deleteRefId.trim()}
                aria-label="Look up order"
              >
                {deleteLoading ? 'Looking up...' : 'Look Up'}
              </button>
              {(preDeleteResult || deleteSummary) && (
                <button
                  style={{ ...primaryBtnStyle, backgroundColor: '#e0e0e0', color: '#333' }}
                  onClick={clearDeleteForm}
                  aria-label="Clear form"
                >
                  Clear
                </button>
              )}
            </div>

            {deleteError && (
              <p style={{ color: '#d32f2f', fontSize: '0.85rem', marginBottom: '0.5rem' }}>{deleteError}</p>
            )}

            {deleteLoading && (
              <p style={{ color: TEXT_MUTED, fontSize: '0.85rem' }}>Loading...</p>
            )}

            {/* Pre-delete details */}
            {preDeleteResult && !deleteSummary && (
              <div
                style={{
                  backgroundColor: BG_SURFACE,
                  border: `1px solid ${BORDER_LIGHT}`,
                  borderRadius: '6px',
                  padding: '1rem',
                  marginBottom: '0.75rem',
                }}
              >
                <p style={{ margin: '0 0 4px', fontSize: '0.85rem' }}>
                  <strong>Purchaser:</strong> {preDeleteResult.purchaserName}
                </p>
                <p style={{ margin: '0 0 4px', fontSize: '0.85rem' }}>
                  <strong>Tickets:</strong> {preDeleteResult.ticketCount}
                </p>
                <p style={{ margin: '0 0 4px', fontSize: '0.85rem' }}>
                  <strong>Guests:</strong> {preDeleteResult.guestNames.join(', ')}
                </p>
                {preDeleteResult.warnings.length > 0 && (
                  <div style={{ marginTop: '0.5rem' }}>
                    {preDeleteResult.warnings.map((w, i) => (
                      <p key={i} style={{ margin: '0 0 2px', fontSize: '0.8rem', color: '#e65100' }}>
                        ⚠️ {w}
                      </p>
                    ))}
                  </div>
                )}

                {/* Reason selection */}
                <div style={{ marginTop: '0.75rem', display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <label style={{ fontSize: '0.85rem', fontWeight: 600, color: TEXT_DARK }}>
                    Reason:
                  </label>
                  <select
                    value={deleteReason}
                    onChange={(e) => setDeleteReason(e.target.value as DeletionReason | '')}
                    style={{ ...inputStyle, minWidth: '180px' }}
                    aria-label="Deletion reason"
                  >
                    <option value="">-- Select reason --</option>
                    <option value="Duplicate">Duplicate</option>
                    <option value="EnteredInError">Entered in Error</option>
                    <option value="Inconsistent">Inconsistent</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                {/* Delete button */}
                <div style={{ marginTop: '0.75rem' }}>
                  <button
                    style={{
                      ...destructiveBtnStyle,
                      opacity: !deleteReason || deleting ? 0.5 : 1,
                      cursor: !deleteReason || deleting ? 'not-allowed' : 'pointer',
                    }}
                    onClick={handleDeleteOrder}
                    disabled={!deleteReason || deleting}
                    aria-label="Delete order"
                  >
                    {deleting ? 'Deleting...' : '🗑️ Delete Order'}
                  </button>
                </div>
              </div>
            )}

            {/* Deletion success summary */}
            {deleteSummary && (
              <div
                style={{
                  backgroundColor: '#e8f5e9',
                  border: '1px solid #a5d6a7',
                  borderRadius: '6px',
                  padding: '1rem',
                }}
              >
                <p style={{ margin: '0 0 8px', fontSize: '0.9rem', fontWeight: 700, color: '#2e7d32' }}>
                  ✅ Order "{deleteSummary.orderReferenceId}" deleted successfully
                </p>
                <p style={{ margin: '0 0 4px', fontSize: '0.85rem' }}>
                  <strong>Purchaser:</strong> {deleteSummary.purchaserName}
                </p>
                <p style={{ margin: '0 0 4px', fontSize: '0.85rem' }}>
                  Tickets deleted: {deleteSummary.ticketsDeleted} | Guests removed: {deleteSummary.guestRecordsDeleted}
                </p>
                <p style={{ margin: '0 0 4px', fontSize: '0.85rem' }}>
                  Registrations: {deleteSummary.registrationsDeleted} | Seats unlinked: {deleteSummary.seatsUnlinked}
                </p>
                <p style={{ margin: '0 0 4px', fontSize: '0.85rem' }}>
                  Check-ins: {deleteSummary.checkInsDeleted} | Meal selections: {deleteSummary.mealSelectionsDeleted}
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Section 3: Consistency Check */}
      <div style={sectionStyle}>
        <div
          style={sectionTitleStyle}
          onClick={() => toggleSection('consistency')}
          aria-expanded={expandedSections.consistency}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && toggleSection('consistency')}
        >
          {expandedSections.consistency ? '▾' : '▸'} 🩺 Consistency Check
        </div>
        {expandedSections.consistency && (
          <div>
            <button
              style={primaryBtnStyle}
              onClick={handleConsistencyCheck}
              disabled={consistencyLoading}
              aria-label="Run consistency check"
            >
              {consistencyLoading ? 'Checking...' : '🩺 Run Consistency Check'}
            </button>

            {consistencyError && (
              <p style={{ color: '#d32f2f', fontSize: '0.85rem', marginTop: '0.5rem' }}>
                {consistencyError}
              </p>
            )}

            {consistencyLoading && (
              <p style={{ color: TEXT_MUTED, fontSize: '0.85rem', marginTop: '0.5rem' }}>Loading...</p>
            )}

            {consistencyReport && !consistencyLoading && (
              <div style={{ marginTop: '0.75rem' }}>
                {/* Totals */}
                <div
                  style={{
                    display: 'flex',
                    gap: '1rem',
                    flexWrap: 'wrap',
                    marginBottom: '0.75rem',
                    fontSize: '0.85rem',
                  }}
                >
                  <span>
                    <strong>Guest Records:</strong> {consistencyReport.counts.guestRecords}
                  </span>
                  <span>
                    <strong>Tickets:</strong> {consistencyReport.counts.tickets}
                  </span>
                  <span>
                    <strong>Orders:</strong> {consistencyReport.counts.orders}
                  </span>
                  <span>
                    <strong>Registrations:</strong> {consistencyReport.counts.registrations}
                  </span>
                </div>

                {consistencyReport.healthy ? (
                  <div
                    style={{
                      backgroundColor: '#e8f5e9',
                      border: '1px solid #a5d6a7',
                      borderRadius: '6px',
                      padding: '0.75rem 1rem',
                      color: '#2e7d32',
                      fontWeight: 600,
                      fontSize: '0.85rem',
                    }}
                  >
                    ✅ All records are consistent
                  </div>
                ) : (
                  <div>
                    {consistencyReport.issues.map((issue, idx) => (
                      <div
                        key={idx}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          marginBottom: '0.5rem',
                          fontSize: '0.85rem',
                        }}
                      >
                        <span
                          style={{
                            ...badgeBase,
                            backgroundColor: '#ffebee',
                            color: '#c62828',
                          }}
                        >
                          {issue.count}
                        </span>
                        <span style={{ color: TEXT_DARK }}>{issue.description}</span>
                        {issue.type === 'unlinked_ticket' && (
                          <button
                            style={{ marginLeft: '8px', padding: '3px 10px', fontSize: '0.75rem', fontWeight: 600, backgroundColor: '#1a6b3c', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                            onClick={async () => {
                              try {
                                const result = await apiClient<{ repaired: number; details: Array<{ fullName: string; ticketNumber: string }> }>('/order-data-management/consistency-check/repair-unlinked-tickets', { method: 'POST' });
                                alert(`Repaired ${result.repaired} tickets. GuestRecords created for: ${result.details.map((d) => d.fullName).join(', ')}`);
                                handleConsistencyCheck();
                              } catch (err: any) {
                                alert(err?.message || 'Repair failed.');
                              }
                            }}
                            aria-label="Repair unlinked tickets"
                          >
                            🔧 Repair
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Section 4: Deletion History */}
      <div style={sectionStyle}>
        <div
          style={sectionTitleStyle}
          onClick={() => toggleSection('history')}
          aria-expanded={expandedSections.history}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && toggleSection('history')}
        >
          {expandedSections.history ? '▾' : '▸'} 📋 Deletion History
        </div>
        {expandedSections.history && (
          <div>
            {historyError && (
              <p style={{ color: '#d32f2f', fontSize: '0.85rem' }}>{historyError}</p>
            )}

            {historyLoading && (
              <p style={{ color: TEXT_MUTED, fontSize: '0.85rem' }}>Loading...</p>
            )}

            {history !== null && !historyLoading && (
              <>
                {history.length === 0 ? (
                  <p style={{ color: TEXT_MUTED, fontSize: '0.85rem' }}>No deletions recorded</p>
                ) : (
                  <table style={tableStyle}>
                    <thead>
                      <tr>
                        <th style={thStyle}>Date</th>
                        <th style={thStyle}>Order Ref</th>
                        <th style={thStyle}>Guests</th>
                        <th style={thStyle}>Revenue Impact</th>
                        <th style={thStyle}>Reason</th>
                        <th style={thStyle}>Deleted By</th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.map((record) => (
                        <tr key={record.id}>
                          <td style={tdStyle}>
                            {new Date(record.deletedAt).toLocaleDateString()}
                          </td>
                          <td style={tdStyle}>{record.orderReferenceId}</td>
                          <td style={tdStyle}>
                            {record.guestNames || `${record.guestCount} guest(s)`}
                          </td>
                          <td style={tdStyle}>
                            ${Number(record.revenueImpact).toFixed(2)}
                          </td>
                          <td style={tdStyle}>{getReasonBadge(record.reason)}</td>
                          <td style={tdStyle}>{record.deletedByName}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
